import React, { useState, Suspense, lazy } from "react";
import "twin.macro";
import { useDebounce } from "use-debounce";
import { FormSection, Legend, Description } from "../styles";
import { P, Alert } from "components/typography";
import Input from "components/forms/input";
import Spinner from "components/spinner";
import { UseFormMethods } from "react-hook-form";
import { OrderFormValues } from "./orderForm.types";
import { Event } from "api/v2/event.types";
import { fieldsToAddress } from "./d2dFormSection";

// Lazy-loaded so @react-google-maps/api ships in its own chunk and is
// only fetched when the user opens a D2D gift form.
const D2dMap = lazy(() => import("./d2dMap"));

const GiftFormSection: React.FC<{
  register: UseFormMethods<OrderFormValues>["register"];
  errors: UseFormMethods<OrderFormValues>["errors"];
  watch: UseFormMethods<OrderFormValues>["watch"];
  isGift?: boolean;
  deliveryType?: string;
  event: Event;
  mustBeGift?: boolean;
}> = ({ register, errors, watch, isGift, deliveryType, event, mustBeGift }) => {
  const watchedGiftMessage = watch("giftMessage");
  const messageLength = watchedGiftMessage?.length || 0;

  // Gift-specific d2d delivery logic
  const isD2dGift = isGift && deliveryType === "d2d";
  const [recipientDeliveryDistance, setRecipientDeliveryDistance] = useState<
    number | undefined
  >();

  const MILES_TO_METERS = 1609.34;
  const DISTANCE_BUFFER = 0.5 * MILES_TO_METERS;
  const rangeInMiles = event?.d2dRadius || 3;

  const watchedRecipientAddress = watch([
    "recipientStreet",
    "recipientCity",
    "recipientState",
    "recipientPostalCode"
  ]);

  const recipientAddress = fieldsToAddress(
    watchedRecipientAddress.recipientStreet || "",
    watchedRecipientAddress.recipientCity || "",
    watchedRecipientAddress.recipientState || "",
    watchedRecipientAddress.recipientPostalCode || ""
  );
  const [debouncedRecipientAddress] = useDebounce(recipientAddress, 500);

  const validateRecipientDistance = () => {
    if (!isD2dGift) return true;
    return (
      recipientDeliveryDistance === undefined ||
      recipientDeliveryDistance <
        rangeInMiles * MILES_TO_METERS + DISTANCE_BUFFER
    );
  };

  const coordinates =
    event.coordinates
      ?.filter(({ lat, lng }) => lat != null && lng != null)
      .map(({ lat, lng, ...rest }) => ({
        ...rest,
        lat: parseFloat(lat as string),
        lng: parseFloat(lng as string)
      })) || [];

  return (
    <>
      <FormSection>
        <Description>
          <Legend>Gift Options</Legend>
          <P.Small tw="text-muted mt-2">
            Send this order as a gift to someone special
          </P.Small>
        </Description>
        <div tw="w-full md:w-3/4">
          <div tw="flex items-center gap-2">
            <input
              id="isGift"
              name="isGift"
              type="checkbox"
              ref={register()}
              tw="accent-primary"
            />
            <P.Small as="label" htmlFor="isGift" tw="cursor-pointer">
              Send as Gift
            </P.Small>
          </div>
          {mustBeGift && !isGift && (
            <Alert.Info tw="mt-3">
              You already have a regular order for this event. Additional orders
              must be sent as gifts.
            </Alert.Info>
          )}
        </div>
      </FormSection>

      {isGift && (
        <div tw="border-l-4 border-info pl-6 animate-fadeIn">
          <FormSection>
            <Description>
              <Legend hasError={!!errors.recipientName}>
                Gift Recipient Details
              </Legend>
              <P.Small tw="text-muted mt-2">
                Required information for gift delivery
              </P.Small>
            </Description>
            <div tw="w-full md:w-3/4 flex flex-col gap-4">
              <div>
                <label
                  htmlFor="recipientName"
                  tw="block text-sm font-medium mb-1"
                >
                  Recipient Name <span tw="text-danger">*</span>
                </label>
                <Input
                  name="recipientName"
                  id="recipientName"
                  ref={register({
                    required: isGift ? "Recipient name is required" : false
                  })}
                  type="text"
                  placeholder="John Doe"
                  tw="w-full"
                />
                {errors.recipientName && (
                  <P.Small tw="text-danger mt-1">
                    {errors.recipientName.message}
                  </P.Small>
                )}
              </div>

              <div>
                <label
                  htmlFor="recipientEmail"
                  tw="block text-sm font-medium mb-1"
                >
                  Recipient Email <span tw="text-danger">*</span>
                </label>
                <Input
                  name="recipientEmail"
                  id="recipientEmail"
                  ref={register({
                    required: isGift ? "Recipient email is required" : false,
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address"
                    }
                  })}
                  type="email"
                  placeholder="recipient@example.com"
                  tw="w-full"
                />
                {errors.recipientEmail && (
                  <P.Small tw="text-danger mt-1">
                    {errors.recipientEmail.message}
                  </P.Small>
                )}
              </div>

              <div>
                <label
                  htmlFor="recipientPhone"
                  tw="block text-sm font-medium mb-1"
                >
                  Recipient Phone <span tw="text-danger">*</span>
                </label>
                <Input
                  name="recipientPhone"
                  id="recipientPhone"
                  ref={register({
                    required: isGift ? "Recipient phone is required" : false
                  })}
                  type="tel"
                  placeholder="555-123-4567"
                  tw="w-full"
                />
                {errors.recipientPhone && (
                  <P.Small tw="text-danger mt-1">
                    {errors.recipientPhone.message}
                  </P.Small>
                )}
              </div>
            </div>
          </FormSection>

          <FormSection>
            <Description>
              <Legend
                hasError={
                  isD2dGift &&
                  (!!errors.recipientStreet ||
                    !!errors.recipientCity ||
                    !!errors.recipientState ||
                    !!errors.recipientPostalCode)
                }
              >
                Recipient {deliveryType === "d2d" ? "Delivery " : ""}Address
                {isD2dGift && <span tw="text-danger"> *</span>}
              </Legend>
              <P.Small tw="text-muted mt-2">
                {isD2dGift
                  ? "Required for door-to-door delivery validation"
                  : "Optional - recipient's address for reference"}
              </P.Small>
            </Description>
            <div tw="w-full md:w-3/4 flex flex-col gap-4">
              {isD2dGift && (
                <>
                  <Alert.Info>
                    Recipient's address must be within {rangeInMiles} miles of
                    our location{coordinates.length > 1 ? "s" : ""} to receive
                    the order.
                  </Alert.Info>
                  {event.pickupLat && event.pickupLng && (
                    <div tw="py-6">
                      <Suspense fallback={<Spinner disabled={false} />}>
                        <D2dMap
                          radius={event.d2dRadius || 3}
                          coordinates={coordinates}
                          lat={parseFloat(event.pickupLat)}
                          lng={parseFloat(event.pickupLng)}
                          fromAddress={`${event.pickupStreet}, ${event.pickupCityStateZip}`}
                          deliveryAddress={debouncedRecipientAddress}
                          setDeliveryDistance={setRecipientDeliveryDistance}
                        />
                      </Suspense>
                    </div>
                  )}
                  {recipientDeliveryDistance !== undefined &&
                    (validateRecipientDistance() ? (
                      <Alert.Success>
                        Recipient's address is within our delivery range! We
                        can't wait to deliver this gift.
                      </Alert.Success>
                    ) : (
                      <Alert.Danger>
                        Recipient's address is{" "}
                        {(
                          (recipientDeliveryDistance || 0) / MILES_TO_METERS -
                          rangeInMiles
                        ).toFixed(2)}{" "}
                        miles outside our delivery range. Sorry, we can't
                        deliver this gift.
                      </Alert.Danger>
                    ))}
                </>
              )}
              <div>
                <label
                  htmlFor="recipientStreet"
                  tw="block text-sm font-medium mb-1"
                >
                  Street Address{isD2dGift && <span tw="text-danger"> *</span>}
                </label>
                <Input
                  name="recipientStreet"
                  id="recipientStreet"
                  ref={register({
                    required: isD2dGift
                      ? "Recipient street is required for delivery"
                      : false,
                    validate: {
                      distance: () =>
                        validateRecipientDistance() ||
                        "Recipient address is outside of delivery range"
                    }
                  })}
                  type="text"
                  placeholder="123 Main St"
                  tw="w-full"
                />
                {errors.recipientStreet && (
                  <P.Small tw="text-danger mt-1">
                    {errors.recipientStreet.message}
                  </P.Small>
                )}
              </div>

              <div tw="flex gap-4">
                <div tw="flex-1">
                  <label
                    htmlFor="recipientCity"
                    tw="block text-sm font-medium mb-1"
                  >
                    City{isD2dGift && <span tw="text-danger"> *</span>}
                  </label>
                  <Input
                    name="recipientCity"
                    id="recipientCity"
                    ref={register({
                      required: isD2dGift
                        ? "Recipient city is required for delivery"
                        : false,
                      validate: {
                        distance: () =>
                          validateRecipientDistance() ||
                          "Recipient address is outside of delivery range"
                      }
                    })}
                    type="text"
                    placeholder="San Francisco"
                    tw="w-full"
                  />
                  {errors.recipientCity && (
                    <P.Small tw="text-danger mt-1">
                      {errors.recipientCity.message}
                    </P.Small>
                  )}
                </div>

                <div tw="w-24">
                  <label
                    htmlFor="recipientState"
                    tw="block text-sm font-medium mb-1"
                  >
                    State{isD2dGift && <span tw="text-danger"> *</span>}
                  </label>
                  <Input
                    name="recipientState"
                    id="recipientState"
                    ref={register({
                      required: isD2dGift
                        ? "Recipient state is required for delivery"
                        : false,
                      validate: {
                        distance: () =>
                          validateRecipientDistance() ||
                          "Recipient address is outside of delivery range"
                      }
                    })}
                    type="text"
                    placeholder="CA"
                    tw="w-full"
                    maxLength={2}
                  />
                  {errors.recipientState && (
                    <P.Small tw="text-danger mt-1">
                      {errors.recipientState.message}
                    </P.Small>
                  )}
                </div>

                <div tw="w-32">
                  <label
                    htmlFor="recipientPostalCode"
                    tw="block text-sm font-medium mb-1"
                  >
                    Zip Code{isD2dGift && <span tw="text-danger"> *</span>}
                  </label>
                  <Input
                    name="recipientPostalCode"
                    id="recipientPostalCode"
                    ref={register({
                      required: isD2dGift
                        ? "Recipient zip code is required for delivery"
                        : false,
                      validate: {
                        distance: () =>
                          validateRecipientDistance() ||
                          "Recipient address is outside of delivery range"
                      }
                    })}
                    type="text"
                    placeholder="94102"
                    tw="w-full"
                  />
                  {errors.recipientPostalCode && (
                    <P.Small tw="text-danger mt-1">
                      {errors.recipientPostalCode.message}
                    </P.Small>
                  )}
                </div>
              </div>
            </div>
          </FormSection>

          <FormSection>
            <Description>
              <Legend>Personal Message (Optional)</Legend>
              <P.Small tw="text-muted mt-2">
                Add a special message to your gift (200 characters max)
              </P.Small>
            </Description>
            <div tw="w-full md:w-3/4">
              <textarea
                name="giftMessage"
                id="giftMessage"
                ref={register({
                  maxLength: {
                    value: 200,
                    message: "Message cannot exceed 200 characters"
                  }
                })}
                placeholder="Happy Birthday! Enjoy this meal on me..."
                tw="w-full min-h-[100px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                maxLength={200}
              />
              <div tw="flex justify-between mt-1">
                <div>
                  {errors.giftMessage && (
                    <P.Small tw="text-danger">
                      {errors.giftMessage.message}
                    </P.Small>
                  )}
                </div>
                {messageLength > 200 ? (
                  <P.Small tw="text-danger">{messageLength}/200</P.Small>
                ) : (
                  <P.Small tw="text-muted">{messageLength}/200</P.Small>
                )}
              </div>
            </div>
          </FormSection>
        </div>
      )}
    </>
  );
};

export default GiftFormSection;
